import React, { useState, useEffect } from 'react';
import { getCachedImage, cacheImage } from '../services/ImageCacheService';
import ImageOverlay from './ImageOverlay';

/**
 * Component that displays a preview image for a given URL
 * Fetches the webpage, finds the largest image, and caches the result
 */
const ImagePreview = ({ url, className = '' }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  useEffect(() => {
    if (!url) return;

    // Check cache first
    const cachedImageUrl = getCachedImage(url);
    if (cachedImageUrl) {
      setImageUrl(cachedImageUrl);
      return;
    }

    // Fetch and process webpage
    fetchImageFromUrl(url);
  }, [url]);

  const fetchImageFromUrl = async (targetUrl) => {
    setLoading(true);
    setError(false);

    try {
      // Try to extract image from URL patterns first (faster and no CORS issues)
      const directImageUrl = tryDirectImageExtraction(targetUrl);
      if (directImageUrl) {
        setImageUrl(directImageUrl);
        cacheImage(targetUrl, directImageUrl);
        setLoading(false);
        return;
      }

      // Use our backend proxy service for all other URLs
      await fetchWithBackendProxy(targetUrl);
    } catch (err) {
      // If all methods fail, just hide the preview gracefully
      console.log('Image preview failed for:', targetUrl, err.message);
      setError(true);
    }

    setLoading(false);
  };

  const tryDirectImageExtraction = (url) => {
    // Check if the URL itself is an allowed image (JPEG, JPG, PNG, GIF, WEBP)
    const imageExtensions = /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i;
    if (imageExtensions.test(url)) {
      return url;
    }

    // Try to extract images from known patterns
    try {
      const urlObj = new URL(url);

      // YouTube thumbnail extraction
      if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
        const videoId = extractYouTubeVideoId(url);
        if (videoId) {
          return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        }
      }

      // Common image hosting patterns
      if (urlObj.hostname.includes('imgur.com')) {
        // Extract imgur image ID and get direct image URL
        const match = url.match(/imgur\.com\/([a-zA-Z0-9]+)/);
        if (match && match[1]) {
          return `https://i.imgur.com/${match[1]}.jpg`;
        }
      }

    } catch (err) {
      console.warn('Failed to parse URL for direct image extraction:', err);
    }

    return null;
  };

  const extractYouTubeVideoId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const fetchWithBackendProxy = async (targetUrl) => {
    // Use our own backend service for CORS proxy
    const proxyUrl = `/api/image-preview?url=${encodeURIComponent(targetUrl)}`;

    try {
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        throw new Error(`Backend proxy failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.imageUrl) {
        setImageUrl(data.imageUrl);
        cacheImage(targetUrl, data.imageUrl);
        return;
      }

      throw new Error(data.error || 'No suitable image found via backend proxy');
    } catch (err) {
      console.warn('Backend proxy fetch failed:', err);
      throw err;
    }
  };


  const handleImageError = () => {
    setError(true);
    setImageUrl(null);
  };

  const handleImageLoad = () => {
    setError(false);
  };

  const handleImageClick = (event) => {
    // Stop event propagation to prevent drag-and-drop or other parent handlers from interfering
    event.stopPropagation();
    event.preventDefault();
    setOverlayOpen(true);
  };

  const handleOverlayClose = () => {
    setOverlayOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleImageClick(event);
    }
  };

  const handleMouseDown = (event) => {
    // Prevent drag from starting when mouse down occurs on image
    event.stopPropagation();
  };

  const handlePointerDown = (event) => {
    // Prevent drag from starting when pointer down occurs on image
    event.stopPropagation();
  };

  if (loading) {
    return (
      <div className={`image-preview loading ${className}`}>
        <div className="image-preview-placeholder">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading image...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !imageUrl) {
    return null; // Don't show anything if no image is available
  }

  return (
    <>
      <div className={`image-preview ${className}`}>
        <img
          src={imageUrl}
          alt="Preview"
          className="image-preview-img"
          onError={handleImageError}
          onLoad={handleImageLoad}
          onClick={handleImageClick}
          onKeyDown={handleKeyDown}
          onMouseDown={handleMouseDown}
          onPointerDown={handlePointerDown}
          loading="lazy"
          role="button"
          tabIndex={0}
          aria-label="Click to view full size image"
        />
      </div>
      <ImageOverlay
        imageUrl={imageUrl}
        alt="Preview"
        isOpen={overlayOpen}
        onClose={handleOverlayClose}
      />
    </>
  );
};

export default ImagePreview;